import React, { useEffect, useState } from 'react';
import AdminLayoutWrapper from '../../components/layout/AdminLayoutWrapper';
import DataTableLayout from '../../components/common/DataTableLayout';
import FilterPanel from '../../components/common/FilterPanel';
import {
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';
import { createFilterChangeHandler, clearFilters, filterData, dataGridProps, createStatusChip, statusConfigs } from '../../utils/tableUtils';

export default function SupportRequestsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ user_email: '', issue_type: '', status: '' });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpen = (req) => {
    setSelected(req);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/support/all/');
        setRequests(Array.isArray(res.data) ? res.data : (res.data.results || []));
      } catch (err) {
        console.error('Fetch support requests failed', err);
        setError('Failed to load');
        showSnackbar('Failed to load support requests', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const issueOptions = [...new Set(requests.map(r => r.issue_type))];
  const statusOptions = [...new Set(requests.map(r => r.status))];

  const filteredRequests = filterData(requests, filters);

  const handleFilterChange = createFilterChangeHandler(setFilters);
  const handleClearFilters = () => clearFilters(setFilters, { user_email: '', issue_type: '', status: '' });

  const filterConfig = [
    {
      field: 'user_email',
      label: 'Email',
      type: 'text',
      placeholder: 'Search by email...'
    },
    {
      field: 'issue_type',
      label: 'Issue Type',
      type: 'select',
      options: issueOptions.map(option => ({ value: option, label: option }))
    },
    {
      field: 'status',
      label: 'Status',
      type: 'select',
      options: statusOptions.map(option => ({ value: option, label: option }))
    }
  ];

  const columns = [
    {
      field: 'user_email',
      headerName: 'User Email',
      flex: 2
    },
    {
      field: 'issue_type',
      headerName: 'Issue Type',
      flex: 1,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          size="small" 
          color="primary" 
          variant="outlined"
        />
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: (params) => {
        const chipProps = createStatusChip(params.value, {
          pending: { label: 'Pending', color: 'warning' },
          resolved: { label: 'Resolved', color: 'success' },
          in_progress: { label: 'In Progress', color: 'info' }
        });
        return <Chip {...chipProps} />;
      }
    },
    {
      field: 'created_at',
      headerName: 'Created',
      flex: 1,
      valueGetter: (params) => params.value ? new Date(params.value).toLocaleDateString() : 'N/A'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              sx={{ p: 0.5 }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  const emptyStateProps = {
    title: 'No support requests found',
    description: 'Try adjusting your filters or check back later.',
    icon: <VisibilityIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
  };

  if (error) {
    return (
      <AdminLayoutWrapper>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100%',
          p: 3
        }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </AdminLayoutWrapper>
    );
  }

  return (
    <AdminLayoutWrapper>
      <DataTableLayout
        title="Support Requests"
        filterPanel={
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterConfig={filterConfig}
          />
        }
        loading={loading}
        isEmpty={!loading && filteredRequests.length === 0}
        emptyStateProps={emptyStateProps}
        mobileFilterOpen={mobileFilterOpen}
        onMobileFilterClose={() => setMobileFilterOpen(!mobileFilterOpen)}
      >
        <DataGrid
          rows={filteredRequests}
          columns={columns}
          {...dataGridProps}
          components={{
            Toolbar: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                p: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <GridToolbarColumnsButton 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarDensitySelector 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                  <GridToolbarExport 
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.50' }
                    }}
                  />
                </Box>
              </Box>
            )
          }}
        />
      </DataTableLayout>

      {/* View Details Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Support Request Details
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ mt: 1 }}>
              <TextField
                label="User Email"
                value={selected.user_email}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Issue Type"
                value={selected.issue_type}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selected.status}
                  label="Status"
                  onChange={(e) => {
                    setSelected({ ...selected, status: e.target.value });
                  }}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="resolved">Resolved</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Subject"
                value={selected.subject}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Message"
                value={selected.message}
                fullWidth
                margin="normal"
                multiline
                rows={4}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Created"
                value={selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button 
            variant="contained" 
            onClick={async () => {
              try {
                await api.patch(`/support/requests/${selected.id}/`, {
                  status: selected.status
                });
                showSnackbar('Status updated successfully');
                // Refresh the data
                const res = await api.get('/support/all/');
                setRequests(Array.isArray(res.data) ? res.data : (res.data.results || []));
                handleClose();
              } catch (err) {
                console.error('Failed to update status:', err);
                showSnackbar('Failed to update status', 'error');
              }
            }}
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayoutWrapper>
  );
}
